import {Dispatch, SetStateAction, useState, useMemo} from 'react';

import {assert} from 'Asserts';
import {SchemaFieldSelectorsDict} from 'notebook/structured-tables/hooks/useSelectorsById';
import {ModalButton} from 'ui-library/modals/Modal';

import {
  ProcessTask,
  ProcessOutput,
  ProcessAsyncRecord,
  FromItemIdsToTables,
  FixFailedRoutingAndMappingStepKey,
  isAsyncDataStartAutomatedMapping,
  isAsyncRecordRoutingRecord,
} from './FixFailedRoutingAndMappingConstants';
import {
  getItemsToMapFromRoutingError,
  getMapperIdToDownstreamSchemaName,
} from './FixFailedRoutingAndMappingUtils';
import {getFixFailedModalButtons} from './getFixFailedModalButtons';
import {useLoadMappingResults} from './useLoadMappingResults';

interface UseFixFailedRoutingAndMappingStateArgs {
  selectedItem: ProcessTask | null | undefined;
  selectorsById: SchemaFieldSelectorsDict | null | undefined;
  onRequestClose: () => void;
}

type StateSetter<T> = Dispatch<SetStateAction<T>>;

interface UseFixFailedRoutingAndMappingStateResult {
  currentPane: FixFailedRoutingAndMappingStepKey;
  selectedRecord: ProcessAsyncRecord | null;
  itemsToMap: Array<ProcessTask> | Array<ProcessOutput> | null;
  itemIdsToMapperId: {[id: string]: Array<string>};
  itemIdsToEndNodeId: {[id: string]: Array<string>};
  fromItemIdsToTables: FromItemIdsToTables | null;
  mapperIdsToDownstreamSchemaName: {[id: string]: string} | null;
  setItemIdsToMapperIds: StateSetter<{[id: string]: Array<string>}>;
  setItemIdsToEndNodeIds: StateSetter<{[id: string]: Array<string>}>;
  setFromItemIdsToTables: StateSetter<FromItemIdsToTables | null>;
  handleSelectedRecord: (asyncRecord: ProcessAsyncRecord) => void;
  getModalButtons: () => Array<ModalButton>;
}

export function useFixFailedRoutingAndMappingState(
  args: UseFixFailedRoutingAndMappingStateArgs
): UseFixFailedRoutingAndMappingStateResult {
  const {selectedItem, selectorsById, onRequestClose} = args;

  const [currentPane, setCurrentPane] = useState(FixFailedRoutingAndMappingStepKey.SELECT_ERRORS);
  const [selectedRecord, setSelectedRecord] = useState<ProcessAsyncRecord | null>(null);
  const [itemsToMap, setItemsToMap] = useState<Array<ProcessTask> | Array<ProcessOutput> | null>(null);
  const [itemIdsToMapperId, setItemIdsToMapperIds] = useState<{[id: string]: Array<string>}>({});
  const [itemIdsToEndNodeId, setItemIdsToEndNodeIds] = useState<{[id: string]: Array<string>}>({});

  const mapperIdsToDownstreamSchemaName = useMemo(
    () => (selectedRecord != null ? getMapperIdToDownstreamSchemaName(selectedRecord) : null),
    [selectedRecord]
  );

  const {fromItemIdsToTables, setFromItemIdsToTables} = useLoadMappingResults({
    currentPane,
    selectedRecord,
    selectorsById,
    itemIdsToMapperId,
    itemsToMap,
  });

  const resetFlow = (): void => {
    setSelectedRecord(null);
    setItemsToMap(null);
    setItemIdsToMapperIds({});
    setItemIdsToEndNodeIds({});
    setFromItemIdsToTables(null);
    setCurrentPane(FixFailedRoutingAndMappingStepKey.SELECT_ERRORS);
  };

  const hasBlockedExpressions = (asyncRecord: ProcessAsyncRecord): boolean =>
    isAsyncRecordRoutingRecord(asyncRecord) &&
    asyncRecord.asyncData.blockedMappersAndExpressions.some(
      ({blockingExpressions}) => blockingExpressions.length > 0
    );

  const handleSelectedRecord = (asyncRecord: ProcessAsyncRecord): void => {
    assert(selectedItem != null, 'Expected item to be selected');
    let nextPane: FixFailedRoutingAndMappingStepKey;
    const tempItemIdsToMapperIds: {[id: string]: Array<string>} = {};
    const tempItemIdsToEndNodeIds: {[id: string]: Array<string>} = {};
    let tempItemsToMap: Array<ProcessTask> | Array<ProcessOutput>;
    // The user should advance to the select downstream nodes modal if they have selected a "routing error"
    // and if the relevant mappers are actually blocked by routing nodes.
    // It's possible for the `start_automated_mapping` task to fail for tasks that do not need to be routed
    // (for example infrastructure issues which cause the celery task to time out) so we detect those kinds of
    // tasks and treat them like "mapping errors" even if the async error is technically a routing error.
    if (isAsyncRecordRoutingRecord(asyncRecord) && hasBlockedExpressions(asyncRecord)) {
      tempItemsToMap = getItemsToMapFromRoutingError(asyncRecord, selectedItem);
      nextPane = FixFailedRoutingAndMappingStepKey.SELECT_DOWNSTREAM_NODES;
      for (const item of tempItemsToMap!) {
        tempItemIdsToMapperIds[item.id] = [];
        tempItemIdsToEndNodeIds[item.id] = [];
      }
    } else {
      const schemaMapperIds = isAsyncDataStartAutomatedMapping(asyncRecord.asyncData)
        ? asyncRecord.asyncData.blockedMappersAndExpressions.map(({schemaMapper}) => schemaMapper.id)
        : [asyncRecord.asyncData.schemaMapper.id];
      nextPane = FixFailedRoutingAndMappingStepKey.TABLE_REVIEW;
      tempItemsToMap = [selectedItem];
      tempItemIdsToMapperIds[selectedItem.id] = schemaMapperIds;
    }
    setSelectedRecord(asyncRecord);
    setCurrentPane(nextPane);
    setItemsToMap(tempItemsToMap);
    setItemIdsToMapperIds(tempItemIdsToMapperIds);
    setItemIdsToEndNodeIds(tempItemIdsToEndNodeIds);
  };

  const getModalButtons = (): Array<ModalButton> =>
    getFixFailedModalButtons({
      currentPane,
      selectedItem,
      selectedRecord,
      itemIdsToMapperId,
      itemIdsToEndNodeId,
      fromItemIdsToTables,
      resetFlow,
      setCurrentPane,
      onRequestClose,
      hasBlockedExpressions,
    });

  return {
    currentPane,
    selectedRecord,
    itemsToMap,
    itemIdsToMapperId,
    itemIdsToEndNodeId,
    fromItemIdsToTables,
    mapperIdsToDownstreamSchemaName,
    setItemIdsToMapperIds,
    setItemIdsToEndNodeIds,
    setFromItemIdsToTables,
    handleSelectedRecord,
    getModalButtons,
  };
}
